import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'common/components/modules/modal';
import { Tooltip } from 'react-tooltip';
import Icon from 'common/components/elements/icons';
import RadioLabel from 'common/components/elements/inputs/radio-label';
import TruncateText from 'common/components/elements/truncate-text';
import { createPortal } from 'react-dom';
import Flag from 'common/components/elements/flags'
import Search from 'common/components/elements/inputs/search';

import cx from 'classnames';

const Select = ({
   isMobile,
   data,
   name,
   activeValue,
   onSelect,
   tooltipId,
   maxHeight,
   type,
   placeholder,
   countryKeys = {
      name: 'display_name',
      value: 'value',
   },
   keys = {
      name: 'name',
      value: 'value',
   },
   isSearchable,
   searchPlaceholder,
   className,
   leftIcon,
   hasError,
   disabled,
}) => {
   const [openChooseModal, setOpenChooseModal] = useState(false)
   const [animate, setAnimate] = useState('up')
   const [tooltipWidth, setTooltipWidth] = useState(310)
   const [options, setOptions] = useState(data)
   const [searchValue, setSearchValue] = useState('')

   let selectRef = useRef(null);
   let searchRef = useRef(null);
   let timeout = null;

   useEffect(() => {
      const handleClickOutside = (event) => {
         const tooltipContent = document.querySelector(`#${ tooltipId }`);
         const currentSelect = selectRef.current;
         if(isMobile){
            return;
         }
         if(tooltipContent && tooltipContent.contains(event.target)) {
            return;
         }
         if(currentSelect && currentSelect.contains(event.target)) {
            return;
         }
         if(openChooseModal) {
            setOpenChooseModal(false)
         }
      }
      if(!isMobile){
         document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
         if(!isMobile){
            document.removeEventListener('mousedown', handleClickOutside);
         }
      }
   }, [selectRef, isMobile, openChooseModal, setOpenChooseModal, tooltipId]);

   useEffect(() => {
      return () =>  clearTimeout(timeout)
   }, [timeout]);



   const onCloseModal = () => {
      if(!isMobile){
         setOpenChooseModal(false)
         return;
      }
      setAnimate('down')
      timeout = setTimeout(() => {
         setOpenChooseModal(false)
         setAnimate('up')
      }, 800)
   }
   const onSearchChange = (value) => {
      setSearchValue(value)
      if(value){
         let key = 'name';
         if('country' === type){
            key = countryKeys?.name || 'display_name';
         }
         let newData = [...data].filter((item) => {
            return item?.[key] && item[key]?.toLowerCase().includes(value?.toLowerCase())
         });
         setOptions(newData);
      } else {
         setOptions(data);
         if(searchRef?.current){
            searchRef.current.focus();
         }
      }
   }
   const view = (
      <div className='flex flex-col py-1'>
         {
            isSearchable && (
               <>
                  <SelectSearch
                     onSearchChange={ onSearchChange }
                     searchPlaceholder={ searchPlaceholder }
                     searchValue={ searchValue }
                     searchRef={ searchRef }
                  />
                  {
                     !options.length && (
                        <div className='flex justify-center items-center p-4 h-[220px]'>
                           <div className='flex flex-col items-center gap-2'>
                              <Icon
                                 name='search'
                                 size='2xl'
                                 color='placeholder'
                              />
                              <span className=' text-base text-placeholder'>No search results</span>
                           </div>
                        </div>
                     )
                  }
               </>
            )
         }

         {
            options && options.map((option, index) => {
               if('country' === type){
                  return (
                     <div
                        key={ option.id || index.toString() }
                        role='presentation'
                        onClick={ _ => {
                           onSelect(option);
                           onCloseModal();
                        } }
                        className='flex py-[10px] px-[14px] cursor-pointer hover:bg-hover'>
                        <div className='flex items-center justify-center h-6 w-6 mr-2'>
                           <Flag className={ `` } height={ 24 } width={ 24 } country={ option[countryKeys?.value || 'value'] } />
                        </div>
                        <TruncateText
                           text={ option[countryKeys?.name || 'display_name'] }
                           textClass='text-major dark:text-major-dark text-base font-normal'
                           textSize='16px'
                           fontWeight='400'
                           width='calc(100% - 32px)'
                           tooltipId='ams-bottom-tooltip'
                        />
                     </div>
                  )
               }
               const checked = option?.[keys.value] === activeValue;
               return (
                  <RadioLabel
                     key={ option.id || index.toString() }
                     id={ `${ tooltipId }_${ option?.[keys.value] }` }
                     checked={ checked }
                     onChange={ () => {
                        onSelect(option);
                        onCloseModal()
                     } }
                     className={ cx({
                        'hover:bg-hover dark:hover:bg-hover-dark gap-2': true,
                        'bg-hover dark:bg-hover-dark': checked,
                     }) }
                  >
                     <div className='flex gap-2 items-center w-full'>
                        {
                           option.icon && (
                              <div className='h-5 w-5 flex items-center justify-center'>
                                 <Icon
                                    name={ option.icon }
                                    size='xl'
                                    color='secondary'
                                    darkColor='secondary-dark'
                                 />
                              </div>
                           )
                        }
                        <TruncateText
                           text={ option?.[keys.name] }
                           textClass='text-major dark:text-major-dark text-base font-normal'
                           textSize='16px'
                           fontWeight='400'
                           width='calc(100% - 28px)'
                           tooltipId='ams-bottom-tooltip'
                        />

                     </div>
                  </RadioLabel>
               )
            })
         }
      </div>
   )
   const getTooltipStyle = () => {
      let style = {
         padding: 0,
         borderRadius: '8px',
         minWidth: `${ tooltipWidth || 310 }px`,
         maxWidth: `${ tooltipWidth || 310 }px`,
         zIndex: 40,
      }
      if(maxHeight){
         style = {
            ...style,
            maxHeight,
         }
      }
      return style;
   }
   return (
      <>
         {
            isMobile && openChooseModal && (
               <Modal
                  animate={ animate }
                  onClose={ onCloseModal }
                  otherClassName='max-h-[80%]'
               >
                  {view}
               </Modal>

            )
         }
         {
            !isMobile &&  createPortal(
               <Tooltip
                  openOnClick
                  variant={ window.site_dark_bg ? 'dark' : 'light' }
                  id={ tooltipId }
                  place='bottom'
                  clickable
                  isOpen={ 'country' === type && isSearchable ? true : openChooseModal }
                  noArrow
                  style={ getTooltipStyle() }
                  offset='4'
                  className={ cx({
                     'shadow dark:shadow-dark border dark:border-divider-dark border-divider overflow-y-auto ams-custom-scrool': true,
                     'max-h-[340px]': 'country' === type,
                     'hidden': !openChooseModal,
                  }) }
               >
                  {
                     openChooseModal && (
                        <>
                           {view}
                        </>
                     )
                  }
               </Tooltip>
               ,
               document.getElementById('tooltips')
            )
         }
         <div
            ref={ selectRef }
            data-tooltip-id={ isMobile ? null : tooltipId }
            className={ cx({
               [`flex justify-between rounded-lg bg-major-dark dark:bg-major lex h-10 items-center py-4 px-[10px] cursor-pointer select-none  border ${ className } hover:bg-hover`]: true,
               'border-error dark:border-error-dark': hasError,
               'border-divider dark:border-divider-dark': !hasError,
            }) }
            onClick={ (e) => {
               if(disabled){
                  return;
               }
               if(!isMobile && !openChooseModal && selectRef.current && selectRef.current.offsetWidth !== tooltipWidth){
                  setTooltipWidth(selectRef.current.offsetWidth);
               }
               setOpenChooseModal(!openChooseModal)
            } }
            role='presentation'
         >
            {
               'country' === type && (
                  <>
                     {
                        activeValue && (
                           <div className='flex flex-1'>
                              <div className='flex items-center justify-center h-6 w-6 mr-2'>
                                 <Flag height={ 24 } width={ 24 } country={ activeValue[countryKeys?.value || 'value'] } />
                              </div>
                              <TruncateText
                                 text={ activeValue[countryKeys?.name || 'display_name'] }
                                 textClass='text-major dark:text-major-dark text-base font-normal'
                                 textSize='16px'
                                 fontWeight='400'
                                 width='calc(100% - 32px)'
                                 tooltipId='ams-bottom-tooltip'
                              />
                           </div>
                        )
                     }
                     {
                        !activeValue && (
                           <span className='text-placeholder text-base leading-[20px]'>
                              {placeholder}
                           </span>
                        )
                     }
                  </>
               )
            }
            <div className='flex gap-2 items-center'>
               {
                  leftIcon && (
                     <div className='h-5 w-5 flex items-center justify-center mr-2'>
                        <Icon
                           name={ leftIcon }
                           size='xl'
                           color='secondary'
                           darkColor='secondary-dark'
                        />
                     </div>
                  )
               }
               {
                  'country' !== type && (
                     <>
                        {
                           !name && (
                              <span className='text-placeholder text-base leading-[20px]'>
                                 {placeholder}
                              </span>
                           )
                        }
                        {
                           name && (
                              <span className='text-major'>{name}</span>
                           )
                        }
                     </>
                  )
               }
            </div>
            <div className='h-5 w-5 flex items-center justify-center ml-2'>
               <Icon
                  name='arrow-down'
                  size='xl'
                  color={ activeValue ? 'secondary' : 'placeholder' }
                  darkColor={ activeValue ? 'secondary-dark' : 'placeholder-dark' }
               />
            </div>


         </div>
      </>
   )
};

const SelectSearch = ({
   searchRef,
   onSearchChange,
   searchPlaceholder,
   searchValue,
}) => {
   useEffect(() => {
      if(!!searchRef?.current){
         searchRef.current.focus();
      }
   }, [searchRef])

   return (
      <div className='p-2'>
         <Search
            onChange={ onSearchChange }
            placeholder={ searchPlaceholder || 'Search' }
            value={ searchValue }
            searchRef={ searchRef }
            isAutoFocus
         />
      </div>
   )
}

SelectSearch.propTypes = {
   searchPlaceholder: PropTypes.string,
   searchValue: PropTypes.string,
   onSearchChange: PropTypes.func,
   searchRef: PropTypes.any,
};

Select.propTypes = {
   isMobile: PropTypes.bool,
   onSelect: PropTypes.func,
   data: PropTypes.array,
   name: PropTypes.string,
   tooltipId: PropTypes.string,
   activeValue: PropTypes.any,
   maxHeight: PropTypes.any,
   type: PropTypes.string,
   placeholder: PropTypes.string,
   countryKeys: PropTypes.object,
   isSearchable: PropTypes.bool,
   searchPlaceholder: PropTypes.string,
   className: PropTypes.string,
   leftIcon: PropTypes.string,
   hasError: PropTypes.bool,
   disabled: PropTypes.bool,
   keys: PropTypes.object,
};
Select.defaultProps = {
   className: '',
   type: 'radio',
   placeholder: 'Select',
   countryKeys: {
      name: 'display_name',
      value: 'value',
   },
}

export default Select
